<template>
  <b-container>
    <b-card class="position-relative">
      <canvas ref="performances-chart" />
    </b-card>
  </b-container>
</template>

<i18n>
{
  "en": {
    "single_cv_model_performance": "Single fold model performance",
    "multi_cv_model_robustness": "All folds model performance",
    "caruana_single_cv_model_performance": "Per model single fold performance",
    "caruana_multi_cv_model_robustness": "Per model all folds performance",
    "caruana_model_combination": "Model combination performance per iteration",
    "caruana_iterations": "Caruana iterations",
    "best_simple_model_score": "Best simple model score"
  },
  "fr": {
    "single_cv_model_performance": "Graphique des performances par type de modèle sur un fold",
    "multi_cv_model_robustness": "Graphique des performances sur tous les folds (pour un point d'abscisse, sont représentés le score minimum, maximum et moyen)",
    "caruana_single_cv_model_performance": "Graphique des performances par type de modèle sur un fold",
    "caruana_multi_cv_model_robustness": "Graphique des performances par type de modèle sur tous les folds",
    "caruana_model_combination": "Graphique des scores obtenus à chaque étape lors de la recherche de la meilleure combinaison de modèle",
    "caruana_iterations": "Itérations Caruana",
    "best_simple_model_score": "Score du meilleur modèle simple"
  }
}
</i18n>

<script>
import _ from 'lodash'
import Chart from 'chart.js'

import chartColors from '@/components/chart_colors.js'

import { subscription_mixin } from '@/components/mixin.js'
import { is_valid } from '@/components/performances_table_caruana.vue'

// *****************************************************************************

class GenericChart {
  constructor(el, nb_iters, title) {
    this.el = el
    this.labels = _.range(nb_iters)
    this.title = title
  }
  init() {
    this.real_chart = new Chart(this.el, {
      type: 'line',
      data: {
        labels: this.labels,
        datasets: [],
      },
      options: {
        responsive: true,
        aspectRatio: 16.0 / 9,
        hover: {
          mode: 'nearest',
          intersect: false,
        },
        plugins: {
          datalabels: {
            display: false,
          },
        },
        title: {
          display: true,
          text: this.title,
        },
      },
    })
  }

  update(datasets, nb_iters) {
    this.labels = _.range(nb_iters)
    this.real_chart.data.labels = this.labels
    this.real_chart.data.datasets = datasets
    this.real_chart.update()
  }
}

// *****************************************************************************

export default {
  name: 'PerformancesChartCaruana',
  components: {},
  mixins: [subscription_mixin],
  props: [
    'project_id',
    'is_caruana',
    'is_cv',
    'is_single_cv',
    'tracking_infos',
    'nb_iters',
  ],
  data() {
    return {
      chart_object: undefined,
      datasets_options_base: {
        cubicInterpolationMode: 'monotone',
        fill: false,
      },
      chart_datasets: [],
    }
  },
  computed: {
    datasets() {
      if (this.is_caruana) {
        if (this.is_cv) {
          if (this.is_single_cv) {
            return this.build_automl_scores_per_model_type_datasets()
          } else {
            return this.build_automl_fullscores_per_model_type_datasets()
          }
        } else {
          return this.build_caruana_datasets()
        }
      } else {
        if (this.is_single_cv) {
          // console.log(JSON.stringify(this.tracking_infos))
          return this.build_automl_scores_datasets()
        } else {
          return this.build_automl_fullscores_datasets()
        }
      }
    },
    title() {
      if (this.is_caruana) {
        if (this.is_cv) {
          if (this.is_single_cv) {
            return this.$t('caruana_single_cv_model_performance')
          } else {
            return this.$t('caruana_multi_cv_model_robustness')
          }
        } else {
          return this.$t('caruana_model_combination')
        }
      } else {
        if (this.is_single_cv) {
          return this.$t('single_cv_model_performance')
        } else {
          return this.$t('multi_cv_model_robustness')
        }
      }
    },
    score_tracking_info() {
      let res = this.fetch_poid_tracking_info('score', this.tracking_infos)
      return res
    },
  },
  watch: {
    nb_iters(new_val, old_val) {
      if (new_val !== old_val) {
        this.check_chart(true)
      }
    },
    datasets(new_val, old_val) {
      this.check_chart()
    },
  },
  beforeCreate() {
    this.colors_correspondence = {
      small_gbdt: chartColors.cyan,
      gbdt: chartColors.blue,
      gbdt_forest: chartColors.green,
      random_forest: chartColors.indigo,
      logistic_regression: chartColors.grey,
      fm: chartColors.pink,
      xgboost: chartColors.yellow,
      lightgbm: chartColors.orange,
      catboost: chartColors.red,
      pytorch_mlp: chartColors.purple,
      naive_bayes_gaussian: chartColors.indigo,
      naive_bayes_bernoulli: chartColors.red,
      poly_svc: chartColors.green,
      rbf_svc: chartColors.purple,
    }
  },
  mounted() {
    this.check_chart()
  },
  beforeDestroy() {
    if (this.chart_object) {
      this.chart_object.real_chart.destroy()
    }
  },
  methods: {
    check_chart(reinit = false) {
      if (this.chart_object === undefined || reinit) {
        if (this.chart_object) {
          this.chart_object.real_chart.destroy()
        }
        this.chart_object = new GenericChart(
          this.$refs['performances-chart'],
          this.nb_iters,
          this.title
        )
        this.chart_object.init()
        this.chart_datasets = []
      }
      for (let dataset of this.datasets) {
        let existing_dataset = _.find(this.chart_datasets, {
          label: dataset.label,
        })
        if (_.isUndefined(existing_dataset)) {
          this.chart_datasets.push(dataset)
        } else {
          existing_dataset.data = dataset.data
        }
      }
      this.chart_object.update(this.chart_datasets, this.nb_iters)
    },

    build_automl_scores_datasets() {
      let datasets = [
        _.merge(
          {
            label: 'Performances',
            backgroundColor: chartColors.blue,
            borderColor: chartColors.blue,
            data: _.map(
              _.filter((this.score_tracking_info || {}).iterations, function(
                x
              ) {
                return (
                  !_.isUndefined(x) && !_.isNaN(x.value) && !_.isNull(x.value)
                )
              }),
              elt => -elt.value
            ),
          },
          this.datasets_options_base
        ),
      ]
      return datasets
    },

    build_automl_fullscores_datasets() {
      let datasets = []

      let min_dataset = _.merge(
        {
          label: 'Minimum',
          backgroundColor: chartColors.red,
          borderColor: chartColors.red,
          data: [],
        },
        this.datasets_options_base
      )
      datasets.push(min_dataset)
      let max_dataset = _.merge(
        {
          label: 'Maximum',
          backgroundColor: chartColors.blue,
          borderColor: chartColors.blue,
          data: [],
        },
        this.datasets_options_base
      )
      datasets.push(max_dataset)
      let avg_dataset = _.merge(
        {
          label: 'Moyenne',
          backgroundColor: chartColors.green,
          borderColor: chartColors.green,
          data: [],
        },
        this.datasets_options_base
      )
      datasets.push(avg_dataset)

      _.forEach(
        _.filter((this.score_tracking_info || {}).iterations, function(elt) {
          return is_valid(elt.values)
        }),
        x => {
          datasets[0].data.push(-_.max(x.values))
          datasets[1].data.push(-_.min(x.values))
          datasets[2].data.push(-_.mean(x.values))
        }
      )
      return datasets
    },

    build_automl_scores_per_model_type_datasets() {
      let datasets = []

      let self = this
      function adapt_data(value, key) {
        let color = self.colors_correspondence[key]
        let dataset = _.merge(
          {
            label: key,
            backgroundColor: color,
            borderColor: color,
          },
          self.datasets_options_base
        )
        dataset.data = _.map(
          _.filter((value || {}).iterations, function(x) {
            return is_valid(x) && is_valid(x.value)
          }),
          elt => -elt.value
        )
        datasets.push(dataset)
      }

      _.forEach(this.tracking_infos, adapt_data)

      return datasets
    },

    build_automl_fullscores_per_model_type_datasets() {
      let datasets = []

      let self = this
      function adapt_data(value, key) {
        let color = self.colors_correspondence[key]
        let dataset = _.merge(
          {
            label: key,
            backgroundColor: color,
            borderColor: color,
          },
          self.datasets_options_base
        )
        dataset.data = _.map(
          _.filter((value || {}).iterations, function(x) {
            return is_valid(x) && is_valid(x.values)
          }),
          elt => -_.mean(elt.values)
        )
        datasets.push(dataset)
      }
      _.forEach(this.tracking_infos, adapt_data)

      return datasets
    },

    build_caruana_datasets() {
      let datasets = [
        _.merge(
          {
            label: this.$t('caruana_iterations'),
            backgroundColor: chartColors.green,
            borderColor: chartColors.green,
          },
          this.datasets_options_base
        ),
        _.merge(
          {
            label: this.$t('best_simple_model_score'),
            backgroundColor: chartColors.red,
            borderColor: chartColors.red,
          },
          this.datasets_options_base
        ),
      ]

      let data = []
      _.forEach(
        _.filter((this.tracking_infos || {}).caruana_iterations, function(x) {
          return is_valid(x) && is_valid(x.value)
        }),
        function(value, key) {
          data[parseInt(key, 10)] = -value.value
        }
      )

      datasets[0].data = data
      datasets[1].data = Array(data.length).fill(data[0])

      return datasets
    },
  },
}
</script>
